import React, { useMemo } from 'react';

// Comps
import ButtonWithChildren from '@arctheme-components/elements/Button/ButtonWithChildren';

// Store
import useCustomerWishlist from '@src/hooks/useCustomerWishlist';
import useLocalWishlistStore from '@src/stores/localWishlistStore';

// Types
import { IGraphQlProduct, ILiquidProduct } from '@src/types/product';

type Props = {
  product: ILiquidProduct | IGraphQlProduct;
  settings?: any;
  listName?: string;
  listType?: string;
  type?: 'plp' | 'pdp';
  addLabel?: string;
  removeLabel?: string;
  isMobile?: boolean;
  onClick?: (product: ILiquidProduct | IGraphQlProduct, isProductInWishlist: boolean) => void;
};

export const ProductWishlist = ({
  product,
  settings,
  listName = 'Products',
  listType = 'products',
  type = 'plp',
  addLabel = 'Add to Wishlist',
  removeLabel = 'Remove from Wishlist',
  isMobile,
  onClick
}: Props) => {

  const shopifyCustomerId = (window as any).customer.id;

  const useText = false;

  // Store
  // Local storage wishlist
  const localLists = useLocalWishlistStore((state) => state.localLists);
  const updateLocalList = useLocalWishlistStore((state) => state.updateLocalList);
  // Customer wishlist
  const { updateCustomerList, createCustomerList, fetchCustomerLists } = useCustomerWishlist();

  // Wishlist that is associated with the provided listType
  const currentList = useMemo(() => {
    // Customer's metaobject data as fallback in case local storage has been cleared but metaobject exists
    return localLists?.find((list) => list.listType === listType);
  }, [localLists]);
  const isProductInWishlist = useMemo(() => {
    return currentList?.products.some((productId) => productId.includes(product.id)) || false;
  }, [localLists]);

  const handleClickWishlist = async () => {
    // If custom onClick prop is provided, use it instead of default behavior
    if (onClick) {
      onClick(product, isProductInWishlist);
      return;
    }

    // Default wishlist behavior
    let newProducts: string[] = [];

    if (isProductInWishlist) {
      // Remove
      newProducts = currentList.products.filter((productId) => !productId.includes(product.id));
    } else {
      // Add
      newProducts = [...(currentList?.products || []), `gid://shopify/Product/${product.id}`];
    }

    // Store in local storage
    updateLocalList({ listName, listType, products: newProducts });

    // If it's logged in customer, update metaobject & metafield
    if (shopifyCustomerId) {
      // Immediately get the returned wishlist metaobject data
      // to use the metaobject ID to update customer's metafield
      const updateListRes = await updateCustomerList({
        handle: `${shopifyCustomerId}-${listType}`,
        listName,
        products: newProducts
      });

      // Add the metaobject to customer's metafield
      // if the currentList doesn't exist
      if (!currentList) {
        const fetchCustomerListsRes = await fetchCustomerLists({ customerId: shopifyCustomerId });
        const newListIds = [...(fetchCustomerListsRes?.map((wishlist) => wishlist.id) || []), updateListRes.id];

        createCustomerList({ customerId: shopifyCustomerId, wishlistLists: newListIds });
      }
    }
  };

  return (
    <ButtonWithChildren
      type='button'
      ariaLabel={isProductInWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
      classes={`product-wishlist flex justify-center ${(type === 'plp' && 'lg:p-4') || ''} ${type === 'plp' && isMobile ? 'items-end' : 'items-center'}`}
      onClick={handleClickWishlist}
    >
      {type === 'pdp' ? (
        <span className='product-wishlist__label brs flex items-center gap-x-2 uppercase text-[#7A7A7A]'>
          {isProductInWishlist ? (
            <>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='8'
                height='7'
                viewBox='0 0 8 7'
                fill='none'
              >
                <path
                  d='M1 3.5H7M'
                  stroke='#7A7A7A'
                  strokeLinecap='round'
                  strokeLinejoin={`round`}
                />
              </svg>
              {removeLabel}
            </>
          ) : (
            <>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='8'
                height='7'
                viewBox='0 0 8 7'
                fill='none'
              >
                <path
                  d='M1 3.5H7M4 0.5V6.5'
                  stroke='#7A7A7A'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
              {addLabel}
            </>
          )}
        </span>
      ) : (
        <>
          <div className='max-lg:hidden'>
            {isProductInWishlist ? (
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='24'
                height='22'
                viewBox='0 0 24 22'
                fill='none'
              >
                <path
                  d='M17.4978 0C16.3722 0.0172091 15.2711 0.326058 14.3058 0.895358C13.3405 1.46466 12.5451 2.27425 12 3.24237C11.4549 2.27425 10.6595 1.46466 9.69417 0.895358C8.72886 0.326058 7.62781 0.0172091 6.50222 0C4.70788 0.0766288 3.01733 0.848179 1.7999 2.14609C0.582466 3.444 -0.0628841 5.16277 0.00483915 6.92689C0.00483915 13.5836 10.9564 21.2719 11.4222 21.5981L12 22L12.5778 21.5981C13.0436 21.2739 23.9952 13.5836 23.9952 6.92689C24.0629 5.16277 23.4175 3.444 22.2001 2.14609C20.9827 0.848179 19.2921 0.0766288 17.4978 0Z'
                  fill='white'
                />
              </svg>
            ) : (
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='24'
                height='22'
                viewBox='0 0 24 22'
                fill='none'
              >
                <path
                  d='M11.5643 3.4877L12 4.26143L12.4357 3.4877C12.9372 2.59697 13.6697 1.85103 14.5598 1.32604C15.4459 0.803451 16.4567 0.518484 17.4909 0.50018C19.1506 0.574814 20.7116 1.29009 21.8354 2.48816C22.9622 3.68942 23.5581 5.2784 23.4955 6.90771L23.4952 6.9173V6.92689C23.4952 8.45932 22.8621 10.0944 21.8521 11.7179C20.8456 13.3358 19.4906 14.9011 18.1076 16.2836C15.3428 19.0476 12.5168 21.0314 12.2923 21.1877C12.2922 21.1877 12.2922 21.1877 12.2921 21.1878L12 21.3909L11.709 21.1886C11.7088 21.1884 11.7086 21.1882 11.7083 21.1881C11.4781 21.0268 8.65474 19.0441 5.89234 16.2828C4.50942 14.9005 3.15437 13.3354 2.14785 11.7176C1.13794 10.0943 0.504839 8.45931 0.504839 6.92689V6.9173L0.504471 6.90771C0.441923 5.2784 1.0378 3.68942 2.16458 2.48816C3.28836 1.29009 4.84941 0.574814 6.50905 0.50018C7.54328 0.518485 8.55407 0.803451 9.44017 1.32604C10.3303 1.85103 11.0628 2.59697 11.5643 3.4877Z'
                  stroke='#7A7A7A'
                />
              </svg>
            )}
          </div>
          <div className='lg:hidden'>
            {useText ? (
              <span className='product-wishlist__label brs uppercase underline'>
                {isProductInWishlist ? `remove` : `add`}
              </span>
            ) : (
              <>
                {isProductInWishlist ? (
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='12'
                    height='10'
                    viewBox='0 0 24 22'
                    fill='none'
                  >
                    <path
                      d='M17.4978 0C16.3722 0.0172091 15.2711 0.326058 14.3058 0.895358C13.3405 1.46466 12.5451 2.27425 12 3.24237C11.4549 2.27425 10.6595 1.46466 9.69417 0.895358C8.72886 0.326058 7.62781 0.0172091 6.50222 0C4.70788 0.0766288 3.01733 0.848179 1.7999 2.14609C0.582466 3.444 -0.0628841 5.16277 0.00483915 6.92689C0.00483915 13.5836 10.9564 21.2719 11.4222 21.5981L12 22L12.5778 21.5981C13.0436 21.2739 23.9952 13.5836 23.9952 6.92689C24.0629 5.16277 23.4175 3.444 22.2001 2.14609C20.9827 0.848179 19.2921 0.0766288 17.4978 0Z'
                      fill='#7A7A7A'
                    />
                  </svg>
                ) : (
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='12'
                    height='10'
                    viewBox='0 0 24 22'
                    fill='none'
                  >
                    <path
                      d='M11.5643 3.4877L12 4.26143L12.4357 3.4877C12.9372 2.59697 13.6697 1.85103 14.5598 1.32604C15.4459 0.803451 16.4567 0.518484 17.4909 0.50018C19.1506 0.574814 20.7116 1.29009 21.8354 2.48816C22.9622 3.68942 23.5581 5.2784 23.4955 6.90771L23.4952 6.9173V6.92689C23.4952 8.45932 22.8621 10.0944 21.8521 11.7179C20.8456 13.3358 19.4906 14.9011 18.1076 16.2836C15.3428 19.0476 12.5168 21.0314 12.2923 21.1877C12.2922 21.1877 12.2922 21.1877 12.2921 21.1878L12 21.3909L11.709 21.1886C11.7088 21.1884 11.7086 21.1882 11.7083 21.1881C11.4781 21.0268 8.65474 19.0441 5.89234 16.2828C4.50942 14.9005 3.15437 13.3354 2.14785 11.7176C1.13794 10.0943 0.504839 8.45931 0.504839 6.92689V6.9173L0.504471 6.90771C0.441923 5.2784 1.0378 3.68942 2.16458 2.48816C3.28836 1.29009 4.84941 0.574814 6.50905 0.50018C7.54328 0.518485 8.55407 0.803451 9.44017 1.32604C10.3303 1.85103 11.0628 2.59697 11.5643 3.4877Z'
                      stroke='#7A7A7A'
                    />
                  </svg>
                )}
              </>
            )}
          </div>
        </>
      )}
    </ButtonWithChildren>
  );
};
